/**
 * Console words. English is primary in the office; Hindi is shown as a secondary line or tooltip
 * where the phone screens already use the word, so supervisors see the same term as the operator.
 */
export interface Label {
  en: string
  hi?: string
}

const STATUS: Record<string, Label> = {
  OPEN: { en: 'Open', hi: 'खुला' },
  CLOSED: { en: 'Closed', hi: 'बंद' },
  RECONCILED: { en: 'Reconciled', hi: 'मिलान हुआ' },
  DRAFT: { en: 'Draft', hi: 'कच्चा' },
  COMMITTED: { en: 'Committed', hi: 'पक्का' },
  IN_TRANSIT: { en: 'In transit', hi: 'रास्ते में' },
  STORED: { en: 'Stored', hi: 'गोदाम में' },
  VOID: { en: 'Void', hi: 'रद्द' },
  NONE: { en: 'No hold' },
  QUARANTINE: { en: 'On hold', hi: 'रोका गया' },
  HELD: { en: 'Held (release)', hi: 'रोका गया' },
  RECALLED: { en: 'Recalled' },
  RESOLVED: { en: 'Resolved', hi: 'सुलझा' },
  WAIVED: { en: 'Waived', hi: 'छोड़ा' },
  PENDING_APPROVAL: { en: 'Waiting for approval', hi: 'मंज़ूरी बाकी' },
  READY: { en: 'Ready to send' },
  SUBMITTING: { en: 'Sending' },
  PROCESSING: { en: 'With the bank' },
  PROCESSED: { en: 'Paid', hi: 'भुगतान हुआ' },
  FAILED: { en: 'Failed', hi: 'असफल' },
  REVERSED: { en: 'Returned' },
  CANCELLED: { en: 'Cancelled' },
  PUBLISHED: { en: 'Published' },
  RETIRED: { en: 'Retired' },
  PRINTED: { en: 'Printed' },
  ISSUED: { en: 'Issued' },
  RETURNED: { en: 'Returned' },
  USED_UP: { en: 'Used up' },
  ACTIVE: { en: 'Active' },
  REVOKED: { en: 'Revoked' },
  VERIFIED: { en: 'Verified' },
  PENDING: { en: 'Pending' },
  DISABLED: { en: 'Disabled' },
  LOADING: { en: 'Loading', hi: 'लद रहा है' },
  RECEIVED: { en: 'Received', hi: 'पहुँचा' },
  RECEIVED_SHORT: { en: 'Received short', hi: 'कम पहुँचा' },
  SEALED: { en: 'Sealed' },
  IN_STORE: { en: 'In store' },
  SENT_TO_LAB: { en: 'Sent to lab' },
  TESTED: { en: 'Tested' },
  DISPOSED: { en: 'Disposed' },
  MATCHES: { en: 'Matches', hi: 'मेल खाता है' },
  MISMATCH: { en: 'Mismatch', hi: 'मेल नहीं' },
  OK: { en: 'OK' },
  LAPSED: { en: 'Lapsed' },
  SOON: { en: 'Due soon' },
  APP: { en: 'App day' },
  PAPER: { en: 'Paper day', hi: 'कागज़ का दिन' },
  MAIN: { en: 'Good', hi: 'अच्छा' },
  CONV: { en: 'Ordinary', hi: 'साधारण' },
  ORDINARY: { en: 'Ordinary', hi: 'साधारण' },
  HOLD: { en: 'On hold', hi: 'रोका गया' },
  // Process and pack
  RUNNING: { en: 'Running', hi: 'चल रहा है' },
  BALANCED: { en: 'Balanced', hi: 'हिसाब ठीक' },
  EXCEPTION: { en: 'Needs a decision', hi: 'जाँच बाकी' },
  PACKING: { en: 'Packing', hi: 'पैकिंग' },
  RELEASED: { en: 'Released', hi: 'जारी' },
  WITHDRAWN: { en: 'Withdrawn' },
  DESTROYED: { en: 'Destroyed' },
  APPROVED: { en: 'Approved' },
  ENTERED: { en: 'Waiting for check' },
  REJECTED: { en: 'Rejected' },
  PASS: { en: 'Pass' },
  FAIL: { en: 'Fail' },
  INCOMPLETE: { en: 'Incomplete' },
  NOT_ACCEPTED: { en: 'Not accepted' },
  NOT_DETECTED: { en: 'Not detected' },
  DETECTED: { en: 'Detected' },
  WITHIN_LIMIT: { en: 'Within limit' },
  OVER_LIMIT: { en: 'Over limit' },
  LOQ_TOO_HIGH: { en: 'Test not sensitive enough' },
  CONSUMED: { en: 'Used up' },
  WAITING: { en: 'Waiting' },
  RELEASE: { en: 'Release' },
  HOLD_DECISION: { en: 'Hold' },
  DISPATCHED: { en: 'Dispatched' },
  DELIVERED: { en: 'Delivered' },
}

export function statusLabel(status: string): Label {
  return STATUS[status.toUpperCase()] ?? { en: humanize(status) }
}

/** SNAKE_CASE → "Snake case". */
export function humanize(code: string): string {
  const s = code.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const EXCEPTION_LABEL: Record<string, Label> = {
  TAG_CONFLICT: { en: 'Same tag on two bags', hi: 'एक टैग दो बोरी पर' },
  TAG_UNKNOWN: { en: 'Unknown tag' },
  TAG_WRONG_KIND: { en: 'Wrong kind of tag' },
  TAG_VOID_SEEN: { en: 'Void tag seen' },
  TAG_REUSED: { en: 'Tag used again' },
  WEIGHT_OUT_OF_RANGE: { en: 'Weight out of range' },
  BAG_COUNT_MISMATCH: { en: 'Bag count does not match', hi: 'बोरी गिनती मेल नहीं' },
  AMOUNT_MISMATCH: { en: 'Phone and server figures differ' },
  OVER_ESTIMATE: { en: 'Over the season estimate' },
  STORAGE_RISK: { en: 'Home storage risk', hi: 'घर पर भंडारण जोखिम' },
  MOISTURE_HIGH: { en: 'Moisture high', hi: 'नमी ज़्यादा' },
  MOISTURE_NOT_READ: { en: 'Moisture not read' },
  FARMER_PHOTO_ADDED: { en: 'Farmer photo taken at the gate', hi: 'गेट पर किसान की फ़ोटो ली गई' },
  SAMPLE_MISSING: { en: 'Sample missing' },
  SAMPLE_UNDERWEIGHT: { en: 'Sample underweight' },
  ORIGIN_MISMATCH: { en: 'Field does not match' },
  RATE_CARD_NOT_IN_FORCE: { en: 'Rate card not in force' },
  SCALE_TEST_FAILED: { en: 'Scale test failed' },
  SCALE_STAMP_EXPIRED: { en: 'Scale stamp lapsed' },
  SCALE_UNREGISTERED: { en: 'Scale not registered' },
  OPERATOR_BLOCKED: { en: 'Operator blocked' },
  SESSION_OVERLAP: { en: 'Two days open at once' },
  SESSION_UNRECONCILED: { en: 'Day does not balance', hi: 'हिसाब नहीं मिला' },
  ROLL_NOT_ASSIGNED: { en: 'Roll not assigned' },
  DEVICE_REVOKED: { en: 'Revoked phone / actor not allowed' },
  COMMAND_CONFLICT: { en: 'Same command, different content' },
  LATE_ENTRY: { en: 'Entered more than a day late' },
  PHOTO_MISSING: { en: 'Photo not uploaded' },
  DUPLICATE_SLIP: { en: 'Slip number used twice' },
  NO_PHONE: { en: 'Farmer has no phone' },
  DELIVERER_PATTERN: { en: 'Same person delivering for many' },
  MULTIPLE_DELIVERIES: { en: 'Many deliveries in a day' },
  DISPUTE: { en: 'Weight dispute', hi: 'वज़न पर विवाद' },
  PAPER_MISMATCH: { en: 'Paper slip does not match', hi: 'पर्ची मेल नहीं' },
  PAYMENT_FAILED: { en: 'Payment failed', hi: 'भुगतान असफल' },
  PAYOUT_UNMATCHED: { en: 'Bank event not matched' },
  PAYOUT_RECON_GAP: { en: 'Bank reconciliation gap' },
  LOW_BALANCE: { en: 'Low bank balance' },
  CUSTODY_RULE: { en: 'Custody rule broken' },
  COUNT_MISMATCH: { en: 'Count does not match', hi: 'गिनती मेल नहीं' },
  TRANSIT_SHORTAGE: { en: 'Short on arrival' },
  TRANSIT_EXTRA: { en: 'Extra on arrival' },
  REWEIGH_VARIANCE: { en: 'Re-weigh outside tolerance' },
  CONTAINER_REPLACEMENT: { en: 'Sack replaced', hi: 'बोरी बदली' },
  RUN_MIXED: { en: 'Run mixed' },
  BALANCE_OUT_OF_BAND: { en: 'Mass balance out of band' },
  LABEL_MISMATCH: { en: 'Label mismatch' },
  LAB_FAIL: { en: 'Lab test failed' },
  OTHER: { en: 'Other' },
}
export function exceptionLabel(type: string): Label {
  return EXCEPTION_LABEL[type] ?? { en: humanize(type) }
}

/**
 * Why a lot is held, in words. V1: only the ownership reasons hold his pay; the quality ones keep
 * the lot out of the brand while he is still paid.
 */
export const LOT_HOLD_LABEL: Record<string, string> = {
  TAG_CONFLICT: 'Same tag on two bags',
  TAG_UNKNOWN: 'Tag from another village or unknown',
  TAG_WRONG_KIND: 'Seal used as a bag tag',
  ORIGIN_MISMATCH: 'Field does not match the farmer',
  DEVICE_REVOKED: 'Phone was switched off',
  STORAGE_RISK: 'Home storage risk',
  SAMPLE_MISSING: 'No sealed sample',
  MOISTURE_NOT_READ: 'No moisture reading',
  MOISTURE_HIGH: 'Too wet: dry first',
  OVER_ESTIMATE: 'Over his season estimate',
  PAPER_DAY: 'Paper day',
  PAPER_MISMATCH: 'Paper slip mismatch',
  BAG_COUNT_MISMATCH: 'Bag count differs',
  SUPERVISOR_HOLD: 'Supervisor hold',
  QUALITY_CHECK: 'Quality check',
  WEIGHT_CHECK: 'Weight check',
  LAB_TEST: 'Needs a lab test',
}

/** V1: lot holds that also hold his pay (ownership in doubt). Mirrors uc-core V1_OWNERSHIP_LOT_HOLDS. */
export const PAY_HOLDING_LOT_HOLDS = [
  'TAG_CONFLICT',
  'TAG_UNKNOWN',
  'TAG_WRONG_KIND',
  'ORIGIN_MISMATCH',
  'DEVICE_REVOKED',
]

export function lotHoldLabel(reason: string): string {
  return LOT_HOLD_LABEL[reason] ?? humanize(reason)
}

/** Common hold reasons a supervisor puts by hand (A–Z0–9_, the server's format). */
export const HOLD_REASONS: Array<{ value: string; label: string }> = [
  { value: 'SUPERVISOR_HOLD', label: 'Supervisor hold' },
  { value: 'QUALITY_CHECK', label: 'Quality check' },
  { value: 'STORAGE_RISK', label: 'Storage risk' },
  { value: 'WEIGHT_CHECK', label: 'Weight check' },
  { value: 'LAB_TEST', label: 'Needs a lab test' },
]

export const CUSTODY_REASON_LABEL: Record<string, string> = {
  GAP: 'Gap in custody',
  DOCK_TOO_LONG: 'On the dock too long',
  NOT_IN_A_BAY: 'Not in a bay',
  BAY_NOT_DECLARED: 'Bay not declared clean',
  VEHICLE_NOT_CLEAN: 'Vehicle not declared clean',
}

const SUBJECT_LABEL: Record<string, string> = {
  UNIT: 'Lot',
  CONTAINER: 'Bag',
  SAMPLE: 'Sample',
  SESSION: 'Collection day',
  SHIPMENT: 'Trip',
  QR: 'Tag',
  QR_CODE: 'Tag',
  QR_BATCH: 'Roll',
  LOCATION: 'Place',
  PAYOUT: 'Payment',
  DEVICE: 'Phone',
  SYNC_COMMAND: 'Phone record',
  FARMER: 'Farmer',
}
/** What an exception is about, in plain words ("CONTAINER" → "Bag"). */
export function subjectLabel(subjectType: string): string {
  return SUBJECT_LABEL[subjectType] ?? humanize(subjectType)
}
